import { MediaQuery } from 'svelte/reactivity';

/**
 * Tri-state theme: system / light / dark.
 *
 * "system" is the default and a real option — it is not the same as "light".
 *
 * Applying the theme is one attribute on <html>, because every semantic token in
 * foundation.css is a light-dark() pair and light-dark() resolves against
 * `color-scheme`. Nothing here touches colours.
 *
 * The matching no-flash script in index.html must set the attribute before first
 * paint — see scaffold/README.md.
 */

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme';

function readStored(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

function createTheme() {
  let preference = $state<ThemePreference>(readStored());
  const prefersDark = new MediaQuery('prefers-color-scheme: dark');

  /** What is actually on screen right now — resolves "system". */
  const resolved = $derived(
    preference === 'system'
      ? prefersDark.current
        ? 'dark'
        : 'light'
      : preference
  );

  return {
    get preference() {
      return preference;
    },
    set preference(next: ThemePreference) {
      preference = next;

      if (next === 'system') {
        localStorage.removeItem(STORAGE_KEY);
        document.documentElement.removeAttribute('data-theme');
      } else {
        localStorage.setItem(STORAGE_KEY, next);
        document.documentElement.setAttribute('data-theme', next);
      }
    },
    get resolved() {
      return resolved;
    },
  };
}

export const theme = createTheme();
